/**
 * The day, form-shaped: every column decided, every cell formatted, every control worded and every
 * refusal explained — one value the dock renders and nothing below it decides anything.
 *
 * `entities/plan-draft` answers what the day IS: which exercises, which sets, which columns, what a
 * ladder cell says, what a removal would break. This module answers what the FORM says. The split is
 * the same one `weekViewModel` makes one layer up, and it is what lets the entity be tested without
 * a locale and the components be written without a rule in them.
 *
 * ── The structure is the week's, not a second copy of it ─────────────────────────────────────
 * Positions, superset brackets and the warm-up block come from `dayCard` — the projection the week
 * canvas already renders — so the dock lists the day in exactly the numbered order the card beside
 * it shows. Re-deriving the bracketing here would be a second copy of a rule with several
 * degenerate cases, and the two would disagree on the first one.
 *
 * What `dayCard` deliberately does not carry is the PARAMETERS, because the week does not draw them.
 * Each row is paired back to its own `ExerciseDraft` by slot token, which is the address the whole
 * app uses.
 *
 * ── What is NOT decided here ─────────────────────────────────────────────────────────────────
 * The two per-exercise guards — may this be removed, may it be linked with the next — are subscribed
 * to per row by the component that draws the menu, because they are plan-scoped questions and there
 * is no stable number of rows to call a hook for. They arrive back here as parameters to
 * `exerciseMenuView`, which is where they are worded. That keeps the wording testable and the
 * subscription narrow.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. `t` arrives as a parameter.
 */

import {
  AMRAP_SET_TYPE,
  BACKOFF_SET_TYPE,
  COLLECTION_CAPS,
  baseCycleOf,
  exerciseAtCycle,
  exerciseLifespan,
  TEXT_LIMITS,
  TOP_SET_TYPE,
  WARMUP_SET_TYPE,
  WORKING_SET_TYPE,
  backoffBlockMissingTop,
  canRemoveSet,
  countingSetCount,
  dayCard,
  dayRestSeconds,
  displayGroupOf,
  exerciseColumns,
  hasAmrapCapConflict,
  hasAuthoredLoad,
  hasWeightField,
  isPercentMode,
  isTopSet,
  muscleLoadByDay,
  measurementValueKey,
  nextSetTypeInCycle,
  overridesDayRest,
  readCellValue,
  resolveGridColumns,
  resolveValueKey,
  setLoadDisplay,
  survivingAmrapCap,
  type CycleSetView,
  type DayDraft,
  type ExerciseDraft,
  type ExerciseRemovalRefusal,
  type MuscleAttributionMap,
  type MuscleDisplayGroup,
  type ResolvedGridColumn,
  type SetDraft,
  type SetGridFieldKey,
} from '~/entities/plan-draft';
import {
  addableFields,
  measurementFieldDefinition,
  type MeasurementFieldKey,
} from '~/shared/measurement';
import { clockDuration } from '~/shared/lib/clockDuration';
import { capitaliseFirst } from '~/shared/lib/localeCase';
import { setTypeBadge, type SetTypeBadgeKind } from '~/shared/lib/setTypeBadge';
import type { ConfirmPrompt } from '~/shared/ui/ConfirmDialog';

import { formatCellNumber, type CellContract } from './setCellValue';

const KEY = 'web:planBuilder.day';

/** Resolve one locale key, with interpolation. Handed in, never imported. */
export type Translate = (key: string, values?: Record<string, string | number>) => string;

/** A control the surface may offer, and the reason it may not. */
export interface ActionView {
  readonly label: string;
  /**
   * Why the control cannot act, or `null` when it can. A disabled control carrying a reason stays
   * FOCUSABLE (`aria-disabled`, never the attribute) — the attribute drops it out of the tab order
   * and takes the explanation with it.
   */
  readonly disabledReason: string | null;
}

/**
 * One way into the library — a NAMED pill that is also a drop target.
 *
 * Two of them, one per block: "+ Ćwiczenie" under the main work and "+ Rozgrzewka" under the warm-up
 * run, exactly as the 07.09 programator mock draws them.
 *
 * ── The copy turned back into a name on 7 September 2026, and the drag did not go ────────────
 * These read "Przeciągnij ćwiczenie, by dodać" from 10 August 2026, when the owner asked that they
 * stop looking like "+ add" buttons and say what they receive. The mock reverses that: both are
 * white pills carrying the block's noun, because the drag was never the only way in and a control
 * whose label describes a gesture is a control a trainer cannot name. Both are still drop targets,
 * and both still open the library on a press.
 *
 * So the two strings stay two, for the reason they always were: the visible LABEL is short and the
 * accessible name has to contain it (WCAG 2.5.3 — a voice-control user says what they can read),
 * with the rest spoken after it. What the tail says has changed with the button: it now names the
 * block the insert lands in and both ways in, rather than explaining a drag that is already drawn.
 *
 * ── The tail is HOST-AWARE, because the drag is not true in every host ───────────────────────
 * It was one string ending "przeciągnij tu pozycję z biblioteki albo naciśnij…" in all four hosts,
 * and in the builder's PANEL that is false: the library is a drawer laid modally over the day, so
 * the form behind it is `inert` and a row dropped out of it lands on nothing (measured, 7 September
 * 2026, and recorded in this slice's `AGENTS.md`). The sentence is true where the library stands
 * BESIDE the day — the fullscreen host and the calendar's 240px column — so the copy is narrowed to
 * the host that cannot honour it rather than deleted from the two that can. `DayProgrammingInput`
 * carries the one boolean; nothing else in this module reads it.
 *
 * Both strings are also whole SENTENCES now, and that is the other half of the same change. They
 * were em-dash-led fragments concatenated onto the visible label to form one — which breaks the
 * owner's standing ban on the em dash and cannot be localised, because the join was the punctuation.
 */
export interface DropZoneView {
  /** The word the pill prints, after its `+` glyph — "Ćwiczenie", "Rozgrzewka". */
  readonly label: string;
  /** Said after it, to assistive technology only: which block, and both paths into it. */
  readonly hint: string;
}

/**
 * Which fixed track a column sits in.
 *
 * The 02.08 gallery's set table is `26px 40px 52px 96px 46px 58px [54px] 78px`, so a column's width
 * is decided by WHAT IT IS rather than by its content — and the five names below are that grid,
 * minus the two leading cells and the trailing controls, which every table always has.
 */
export type ColumnTrack = 'reps' | 'weight' | 'score' | 'rest' | 'extra';

export interface ColumnView {
  readonly key: string;
  readonly label: string;
  readonly emphasis: 'metric' | 'secondary';
  readonly track: ColumnTrack;
  /**
   * How this column is closed FROM ITS OWN HEADER — the gallery's `headCellExtra`, which draws a
   * `×` beside "Tempo" and nothing beside the four columns an exercise starts with.
   *
   * `null` on a starting column, which is closed from "+ Parametr" instead. That is the whole rule:
   * a column the trainer added carries its own undo, and one that was always there does not need a
   * second control beside the panel that governs it.
   */
  readonly close: ColumnActionView | null;
}

/** One entry of the "Parametry serii" panel behind the "+ Parametr" header. */
export interface ParameterToggleView {
  readonly field: SetGridFieldKey;
  readonly label: string;
  /** "kg lub % 1RM", "3-1-1" — the gallery prints a shape beside most of them. `null` when it does not. */
  readonly hint: string | null;
  readonly isOn: boolean;
  /** Why it cannot be turned off — `Reps` is required by the wire. `null` when it can. */
  readonly lockedReason: string | null;
  /** What to ask before turning it off, when doing so would DISCARD authored values. */
  readonly confirm: ConfirmPrompt | null;
}

/** One row of an exercise's own contribution to the day's load — the gallery's "Wkład do obciążenia". */
export interface ContributionView {
  readonly group: MuscleDisplayGroup;
  readonly label: string;
  readonly points: string;
  readonly barPercent: number;
}

export interface SetCellView {
  readonly key: string;
  readonly contract: CellContract;
  /** The value, formatted for the locale. `''` for an empty cell — never `0`. */
  readonly text: string;
  /** What a screen reader hears instead of a column header it cannot see from inside a cell. */
  readonly ariaLabel: string;
  /** "kg", "% 1RM", "% topu", "s" — or `null` for a unitless field. */
  readonly suffix: string | null;
  /**
   * The estimate under a derived load — the canon's italic "~119 kg". `null` when there is nothing
   * honest to derive: a top prescribed as a percentage of 1RM has no kilograms until the client's
   * 1RM is known, and inventing one is what the canon forbids.
   */
  readonly derived: string | null;
  readonly emphasis: 'metric' | 'secondary';
  readonly inputMode: 'decimal' | 'numeric' | 'text';
  /**
   * The sentence printed INSTEAD of a field, because this cycle's value does not exist yet.
   *
   * Non-null exactly on the progressing cell of an exercise whose rule resolves against the client's
   * own history at workout start — a real max, a previous execution, an AMRAP. The matrix draws a
   * stand-in for those because a grid has to draw something; a form that let one be edited would be
   * offering to pin an estimate as a prescription. `null` everywhere else, and always in the builder.
   */
  readonly resolvedNote: string | null;
  /**
   * Where a commit goes.
   *
   * `null` — onto the authored set, which every cycle reads. That is the builder's only mode, and in
   * the calendar's cycle mode it is every cell the exercise does not PROGRESS on: reps, rest, tempo
   * and the rest are flat across cycles and the draft models no per-cycle value for them.
   *
   * Non-null — a pin at this cycle, leaving the curve alone. Only the progressing field (and a
   * back-off descent's share of the top) can carry one, because those are the four fields
   * `ProgressionOverride` models and the only ones `overrideWireInputs` emits.
   */
  readonly pin: { readonly cycleIndex: number; readonly baseCycle: number } | null;
}

/**
 * Re-exported from `~/shared/lib/setTypeBadge`, which holds the ONE letter-and-colour table — the
 * assigned-plan calendar's session panel reads the same set types back off a different endpoint, and
 * a trainer who authors a `T` here must not meet a `B0` there.
 */
export type SetBadgeKind = SetTypeBadgeKind;

export interface SetBadgeView {
  readonly letter: string;
  readonly kind: SetBadgeKind;
  /** "Seria robocza" — the badge's accessible name, so the letter is never the only channel. */
  readonly label: string;
  /**
   * The type one click away, worded. `null` for an inbound `Dropset`, which this build does not
   * author: it renders raw and round-trips unchanged rather than being silently rewritten.
   */
  readonly cycleLabel: string | null;
  /**
   * The wire type that click writes, beside the words for it. Carried rather than re-derived at the
   * badge, because the badge would have to know the cycle's order to do that — and there is one
   * cycle, in `nextSetTypeInCycle`, which is where it stays.
   */
  readonly cycleSetType: string | null;
}

/**
 * One entry of the type menu — a row shaped like mobile's own "Typ serii" list: the type's badge in
 * its own colour, its name, and one line saying what the type DOES. `topSetIndex` is only ever set
 * on the back-off entries.
 *
 * The letter and the kind are carried rather than re-derived at the menu for the same reason
 * `cycleSetType` is: the menu would otherwise have to know the mapping from a wire type to a badge,
 * and there is one such mapping, in `badgeOf`, which is where it stays.
 */
export interface SetTypeChoiceView {
  readonly key: string;
  readonly label: string;
  /** The badge letter — "R", "W", "T", "B", "A". */
  readonly letter: string;
  /** Which of the role colours the row's badge wears. */
  readonly kind: SetBadgeKind;
  /** The line under the name. Always present: a row with no explanation is the one nobody picks. */
  readonly description: string;
  /**
   * The whole row as one accessible name — the label, the description, and the word "current" when
   * it is. The description is not decoration, so it belongs in the name rather than beside it; and
   * the tick and the accent fill that mark the current row are both visual, so the state needs a
   * word of its own.
   */
  readonly ariaLabel: string;
  readonly setType: string;
  readonly isCurrent: boolean;
  /**
   * True on a choice the menu shows but will not take — the back-off row of an exercise with no top
   * set. Shown rather than hidden, dimmed rather than removed: the reason is the description, and a
   * type that vanishes from the menu teaches nothing (owner decision, 3 Aug 2026 — one back-off
   * entry, gated on a top existing, never a list of ways to conjure one).
   */
  readonly isRefused: boolean;
  /**
   * Kept for the menu's dispatch into `makeBackoffSet` — always `null` now: the one back-off choice
   * binds the block's EXISTING top. The variants that named a candidate set or minted a fresh top
   * were removed with the owner's words above.
   */
  readonly topSetIndex?: number | null;
}

export interface SetRowView {
  readonly key: string;
  readonly setIndex: number;
  /** "Seria 3" — the row's name in the grid, and the prefix of every cell's label. */
  readonly label: string;
  readonly badge: SetBadgeView;
  readonly typeChoices: readonly SetTypeChoiceView[];
  readonly cells: readonly SetCellView[];
  /** The AMRAP two-cap shape the backend refuses, named where the trainer can see it. `null` usually. */
  readonly warning: string | null;
  readonly duplicate: ActionView;
  readonly remove: ActionView;
  readonly actionsLabel: string;
  /** "Typ serii" — the heading over the type menu's five rows. See `SetTypeMenu`. */
  readonly typeMenuTitle: string;
}

export interface ExerciseView {
  readonly key: string;
  readonly slotToken: string;
  /** `1`, `2a`. `null` on a warm-up a degenerate group left standing — it holds no position. */
  readonly ordinal: string | null;
  readonly name: string;
  readonly thumbnailUrl: string | null;
  /** "Siła" — drawn only in fullscreen, where there is room for it. `null` when unknown. */
  readonly typeLabel: string | null;
  readonly muscle: { readonly group: MuscleDisplayGroup; readonly label: string } | null;
  readonly isWarmup: boolean;
  readonly columns: readonly ColumnView[];
  /** The `#` column's real name — the glyph is not one a screen reader can use. */
  readonly setColumnLabel: string;
  /** "Typ" — the badge column's header. */
  readonly typeColumnLabel: string;
  /** The trailing column of row controls, which the canon draws with an empty header. */
  readonly actionsColumnLabel: string;
  readonly rows: readonly SetRowView[];
  /**
   * Why the table has no columns at all, or `null`. A movement whose whole contract is `Tempo` is
   * the real case: tempo is a first-class dictionary field with no numeric set cell, so the honest
   * answer is a statement rather than an empty grid.
   *
   * A SHORT statement plus the reason behind an ⓘ, since 7 September 2026. It was one explanatory
   * sentence printed inside the card, carrying an em dash the owner bans outright and an
   * explanation his standing rule puts behind an ⓘ or removes.
   */
  readonly noColumns: {
    readonly label: string;
    readonly hint: string;
    readonly hintLabel: string;
  } | null;
  /** A ladder with descents and no top — a shape only the wire can produce. Named, never repaired. */
  readonly ladderWarning: string | null;
  readonly note: {
    readonly value: string;
    /** The field's own accessible name, which carries the movement so a list of them is telling apart. */
    readonly label: string;
    readonly hint: string;
    readonly maxLength: number;
  };
  readonly addSet: ActionView;
  readonly menuLabel: string;
  /** Whether the exercise prescribes load as a fraction of 1RM — the menu's toggle reads it. */
  readonly isPercentMode: boolean;
  readonly measuresWeight: boolean;
  readonly showsRestColumn: boolean;
  /** The "Parametry serii" panel behind the table's "+ Parametr" header. */
  readonly columnMenu: ColumnMenuView;
  /**
   * The load column's unit, as the control the gallery draws INSIDE its header — "· kg ▾".
   *
   * This is the ONE place the kg ↔ %1RM switch lives now. It used to be an entry in the ⋯; the
   * gallery draws it in the header, and carrying it in both would be two paths to one act.
   *
   * `null` on an exercise with no load column at all, where there is no unit to state.
   */
  readonly unit: { readonly label: string; readonly toggleLabel: string } | null;
  /**
   * What this exercise puts into the day's load module, group by group — the gallery's "Wkład do
   * obciążenia". Empty for a warm-up, which contributes nothing by role.
   */
  readonly contribution: readonly ContributionView[];
  /** "Wkład do obciążenia" — the group's own heading. */
  readonly contributionTitle: string;
  /**
   * "3 × 10" — the whole prescription in one line, printed in the card's head while it is COLLAPSED.
   *
   * The collapse itself is component state; this string is not. It reads the set count against the
   * FIRST column's first cell, so a plank collapses to "2 × 45 s" and a run to "3 × 400 m" rather
   * than to a repetition count neither of them prescribes. `null` where there is nothing to
   * summarise — an exercise with no numeric column at all.
   */
  readonly collapsedSummary: string | null;
  /** "Zwiń Wyciskanie…" / "Rozwiń Wyciskanie…" — the fold control's two accessible names. */
  readonly fold: { readonly collapse: string; readonly expand: string };
  /**
   * The superset-selection checkbox on this card: its accessible name, and why it may not be ticked.
   *
   * A warm-up is the only card that refuses: it is never pulled into a superset, because a group
   * carrying one is a mini-group and a different concept. A card already inside a bracket ticks
   * like any other and the join absorbs it — see `groupAsSuperset`.
   */
  readonly supersetPick: { readonly label: string; readonly refusalReason: string | null };
  /**
   * This lift carries a warm-up of its own — the mini-group's prep box.
   *
   * Read by the removal's confirm on a running plan, which has to say that the ramp goes with the
   * lift (`normaliseGroups` drops a dedicated warm-up whose work exercise leaves). Mobile computes
   * the same fact in `DaysView.removeExerciseVariant`, before its reducer normalises the day.
   */
  readonly hasDedicatedWarmup: boolean;
  /**
   * The head's `Rozgrzewka` chip — one press gives THIS movement its own warm-up, the mini-group.
   *
   * `null` on a card that cannot have one, or that already has one:
   *
   * · a WARM-UP, because a warm-up does not get a warm-up;
   * · a card already inside a SUPERSET, which the mock hides the chip on (`prepBtn`'s own
   *   `display: inGrp ? 'none'`). The card knows whether it is bracketed and the view does not, so
   *   that half is `ExerciseBlock`'s to apply — see its header;
   * · a card that already HOLDS a prep box (owner, 8 September 2026). This one is a departure from
   *   the mock and is recorded as one: `prepBtn` hides on `inGrp` and on nothing else there, so
   *   pressing it again is how a second ramp reaches a lift in the mock. Withholding it leaves this
   *   build with no path to a second prep, because the ⋯'s role move goes to the DAY's warm-up run
   *   rather than into a box. Raised in the report; reversing it is deleting `hasWarmup` from the
   *   condition in `toExerciseView`.
   *
   * Two strings, because the chip prints one word and has to be NAMED with more: a panel holding six
   * of them offers six controls called "Rozgrzewka" otherwise, and voice control cannot pick between
   * them. The visible word is contained in the name, as WCAG 2.5.3 requires.
   *
   * The word was taken away on 15 September 2026 and put back on 16 September: the owner's ask that
   * the head's small controls become glyphs did not survive meeting the row, where an offer nothing
   * else on the card makes had become a fourth identical square.
   */
  readonly addWarmup: { readonly label: string; readonly ariaLabel: string } | null;
}

/** One column the ⋯ can open or close. */
export interface ColumnActionView {
  readonly field: SetGridFieldKey;
  /** The column's own header word — "Ciężar", "% 1RM" in percent mode, "Przerwa". */
  readonly label: string;
  /**
   * What to ask before closing it, or `null` to close it straight away.
   *
   * Non-null exactly when the removal would DISCARD something authored. It is on the entry rather
   * than decided by the component because it is a question about the plan, and the component holds
   * no plan.
   */
  readonly confirm: ConfirmPrompt | null;
}

/** The "Parametry serii" panel: every column the exercise could carry, ticked or not, plus its note. */
export interface ColumnMenuView {
  /** "+ Parametr" — the header cell that opens it. */
  readonly triggerLabel: string;
  /** "Parametry serii" — the panel's own heading, and its accessible name. */
  readonly title: string;
  /** "Wyłączony parametr znika z tabeli w całej fazie — wpisane wartości zostają zapamiętane." */
  readonly hint: string;
  /** Every measurement column the exercise measures, plus REST, in the dictionary's order. */
  readonly toggles: readonly ParameterToggleView[];
}

/**
 * The blue box a lift's own warm-ups stand in, INSIDE its card — the mock's `prepBox`.
 *
 * A mini-group is a warm-up bracketed to the one exercise it prepares, and until 8 September 2026
 * this feature drew it the way the week card does: the members side by side inside a frame, each
 * one a row of the day's list. The mock draws the same data as one card — the lift's — with its
 * ramps folded into a railed box above its head. Same operation, same tree, a different picture; see
 * `ExerciseBlock`'s header for the drawing and `DayForm` for where it is placed.
 */
export interface PrepBoxView {
  /** "Rozgrzewka do ćwiczenia" — the box's own heading, as the mock prints it. */
  readonly title: string;
  /** "2 pozycje" — the mock's `prepCount`, beside the heading. */
  readonly count: string;
  /**
   * The box's accessible name, which carries the LIFT — six boxes in a panel all headed
   * "Rozgrzewka do ćwiczenia" name nothing a screen reader can tell apart.
   */
  readonly label: string;
  /** The strip the whole card is dragged by while it holds preps — the mock's `grpBar`. */
  readonly groupLabel: string;
  readonly members: readonly ExerciseView[];
}

export type DayBlockView =
  | {
      readonly kind: 'single';
      readonly key: string;
      readonly exercise: ExerciseView;
      /**
       * This exercise's own warm-ups, drawn inside its card. `null` on an ordinary single.
       *
       * A mini-group is a `single` here BECAUSE of where the mock puts it: one card, holding its
       * ramps. It is still one list position and still one block to a move, which is what it always
       * was — nothing about the tree or the operations changed with the drawing.
       */
      readonly preps: PrepBoxView | null;
    }
  | {
      readonly kind: 'superset';
      readonly key: string;
      readonly ordinal: string;
      readonly label: string;
      /** "2a → 2b bez przerwy" — what the frame means, beside its own word. */
      readonly note: string;
      readonly members: readonly ExerciseView[];
    };

export interface MuscleBarView {
  readonly group: MuscleDisplayGroup;
  readonly label: string;
  readonly points: string;
  readonly percent: string;
  readonly barPercent: number;
}

export interface DayProgrammingView {
  readonly slotToken: string;
  /** "Środa" — the weekday the day occupies. `null` on a day carrying no weekday. */
  readonly weekdayLabel: string | null;
  /** The session's name, or the untitled fallback. */
  readonly title: string;
  /** Exactly what was authored, so opening the field never offers to save the placeholder. */
  readonly authoredTitle: string | null;
  readonly nameFieldLabel: string;
  readonly nameMaxLength: number;
  /** "5 ćw. · 16 serii · ~62 min" — the duration joined only when one was authored. */
  readonly counts: string;
  /**
   * The day's warm-up run, as the 02.08 gallery draws it: a blue-railed block holding FULL exercise
   * cards, not a list of names.
   *
   * PR-B2C drew names only, because the 01.08 canon did. This gallery prescribes warm-ups — reps,
   * load, RPE and rest, on `R`-badged rows — and the store has always carried them: a `Warmup` role
   * exercise holds real sets, and dropping them from the surface meant a trainer could see "6 min"
   * on the phone and nothing at all here.
   */
  readonly warmup: {
    readonly title: string;
    /**
     * "3 pozycje" — the run's own count, beside its title.
     *
     * **The mock prints a DURATION with it ("3 pozycje · 8 min", and "ROZGRZEWKA … 8 MIN" on the
     * week card) and this does not. Deviation, with its reason, established at the contract on
     * 7 September 2026.** Nothing on the wire carries a warm-up duration: the only estimate there
     * is is `SessionTemplateResponse.estimatedDurationMinutes`, which is the WHOLE session's and is
     * authored, and a warm-up run is not a wire entity at all — the role sits on each exercise, so
     * the run has no id, no container and nothing to hang a figure on. The only per-item time is a
     * set's own `prescribedDurationSeconds`, which is a prescription for a plank rather than an
     * estimate of anything.
     *
     * Deriving one from set counts is what execution ADR §1 forbids in as many words, and
     * `DayDraft.durationMinutes` carries the same rule: "Nothing estimates one from set counts, and
     * a day carrying none simply shows no duration." So the count stands alone and the gap is
     * flagged rather than filled. If the owner wants the figure, it is a backend field first.
     */
    readonly counts: string;
    /**
     * Why 25 sets over 29 rows is correct — behind the run's own ⓘ, and drawn in EVERY host since
     * 7 September 2026.
     *
     * It was printed only in fullscreen, which is unreachable, and the week card carried a
     * "POZA LICZNIKIEM SERII" caption the mock does not draw. The fact is worth stating once, where
     * the counting is; a caption on every card of every week is not once. `hintLabel` is the ⓘ's
     * question — the owner's standing rule is that an explanation lives under an ⓘ or it goes.
     */
    readonly footnote: string;
    readonly footnoteLabel: string;
    readonly exercises: readonly ExerciseView[];
    /** The dashed zone that closes the run — the warm-up's one way in from inside the form. */
    readonly drop: DropZoneView;
    /** "Wstaw szablon rozgrzewki" — offered, and refused with its reason until templates exist. */
    readonly menuLabel: string;
  };
  readonly mainTitle: string;
  /** "8 ćwiczeń · 25 serii" — the main work's own counts, beside its title. */
  readonly mainCounts: string;
  readonly blocks: readonly DayBlockView[];
  readonly muscles: readonly MuscleBarView[];
  readonly muscleTitle: string;
  readonly muscleTotal: string;
  readonly sessionNote: {
    readonly value: string;
    readonly label: string;
    /**
     * "Dodaj notatkę do całej sesji…" — the mock's own invitation, standing in the empty field.
     *
     * It REPLACES the sentence that used to sit under the label ("Widoczna dla klienta na starcie
     * treningu"). The 07.09 mock draws a dashed box with a placeholder and no explanation, and this
     * app's standing rule is that an explanation lives under an ⓘ or it goes — a line saying who
     * reads the note is not worth an ⓘ of its own beside the day's rest.
     */
    readonly placeholder: string;
    readonly maxLength: number;
  };
  readonly dayRest: {
    readonly label: string;
    /**
     * What changing the seed restamps. It is behind an ⓘ since the 07.09 round rather than standing
     * as a paragraph under the field — the owner's standing rule, and the mock's foot has room for
     * one row, not three lines.
     */
    readonly hint: string;
    /** The ⓘ's accessible name — a question, because "info" names nothing. */
    readonly hintLabel: string;
    readonly text: string;
    readonly seconds: number;
  };
  /** The floating action that closes the main-work list. */
  readonly mainDrop: DropZoneView;
  /**
   * A day with nothing in it at all, worded for the card the panel draws instead of a list.
   *
   * Two strings rather than one, because the state is two statements: what this day IS, and what
   * follows from it at save time. `null` on a day that has an exercise. One object rather than two
   * nullable fields, so no caller can draw half of the state.
   */
  readonly emptyDay: { readonly title: string; readonly hint: string } | null;
  /**
   * The standing sentence in the panel host's foot, worded for what this form is editing.
   *
   * In the builder it is the phase scope it has always been. In the calendar it is the owner's own
   * line sending a trainer who wants to rebuild the plan back to the builder — this dock is for
   * correcting one day.
   */
  readonly scopeNote: string;
  /**
   * What a cycle-scoped edit reaches, said once at the head of the list. `null` in the builder.
   *
   * It names the field the exercise progresses on as the one that can be held to a single week, and
   * says plainly that everything else is the same in every week — because it is: the draft models a
   * per-cycle value for the four progression fields and for nothing else, so a reps change here is a
   * reps change everywhere and a form that let that pass unsaid would be lying by omission.
   */
  readonly cycleNote: string | null;
}

/**
 * WHICH cycle the form renders, when it renders one.
 *
 * Absent or `null` is the builder: the authored base, byte for byte what it has always drawn. Set is
 * the assigned-plan calendar, opened on one session — the set table then shows that week's numbers.
 *
 * The three travel together because none of them is usable alone: the phase's span is the frame each
 * exercise resolves its own activation window inside, and the absolute cycle is meaningless without
 * it. One optional field rather than three is what makes a half-set state unrepresentable.
 */
export interface DayCycleContext {
  /** ABSOLUTE across the plan, 1..Σ cycleCount — the coordinate the wire addresses overrides by. */
  readonly cycleIndex: number;
  /** The first absolute cycle of the phase this day sits in. */
  readonly phaseStartCycle: number;
  readonly phaseCycleCount: number;
}

export interface DayProgrammingInput {
  readonly day: DayDraft;
  readonly attribution: MuscleAttributionMap;
  /** Exercise id → its cover still. Absent ids simply draw the neutral placeholder. */
  readonly thumbnails: ReadonlyMap<number, string | null>;
  /** Exercise id → its type's i18n key, or `null`. */
  readonly typeLabels: ReadonlyMap<number, string | null>;
  readonly t: Translate;
  readonly locale: string;
  readonly cycle?: DayCycleContext | null;
  /**
   * Whether the library this day adds from lies OVER the day rather than beside it.
   *
   * True in the builder's panel alone, where the catalogue is a modal drawer: the form behind it is
   * `inert`, so a library row cannot be dragged into the list and the two "+" pills must not say it
   * can. `false` — the default — is the fullscreen host and the calendar's column, where the drag
   * works and the sentence is true. See `DropZoneView`.
   */
  readonly libraryIsModal?: boolean;
}

/**
 * A fixed Monday, used only to ask `Intl` for a weekday name — the same construction
 * `weekViewModel` uses, and for the same reason: the mobile app's static PL/EN tables exist because
 * Hermes throws on `Intl.DateTimeFormat` with a `timeZone`, and browsers do not.
 */
function weekdayLabel(locale: string, dayOfWeek: number | null): string | null {
  if (dayOfWeek === null || dayOfWeek < 1 || dayOfWeek > 7) return null;
  const name = new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
    new Date(2024, 0, dayOfWeek),
  );
  // Polish writes weekday names lower-case and `Intl` returns them that way; the gallery prints
  // "Poniedziałek" as the day modal's own title, where it is a heading rather than a date. The week
  // canvas needs no such fix because CSS upper-cases its column headers outright. Through
  // `shared/lib/localeCase`, which is the rule's one home — `charAt(0)` also splits a surrogate
  // pair, which the shared one does not.
  return capitaliseFirst(name, locale);
}

function formatPoints(locale: string, points: number): string {
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(points);
}

function muscleLabel(t: Translate, group: MuscleDisplayGroup): string {
  return t(`web:planBuilder.muscle.${group}`);
}

function badgeOf(t: Translate, set: SetDraft): SetBadgeView {
  const badge = setTypeBadge(set.setType);
  if (badge.kind === 'dropset') {
    // Recognised, named and coloured — but not cycled. The web does not author a drop set, and a
    // click that relabelled one would rewrite what the phone wrote.
    return {
      ...badge,
      label: t(`${KEY}.setTypeBadgeA11y`, { label: t(`${KEY}.setType.dropset`) }),
      cycleLabel: null,
      cycleSetType: null,
    };
  }
  if (badge.kind === 'raw') {
    // A type a LATER BACKEND adds, which neither client knows yet — the drop set is handled above.
    // It renders with its own initial, neutral, and does not cycle: silently relabelling a set this
    // app cannot read would rewrite a plan it does not understand.
    return {
      ...badge,
      label: t(`${KEY}.setTypeBadgeA11y`, {
        label: t(`${KEY}.setType.raw`, { type: (set.setType ?? '?').trim() }),
      }),
      cycleLabel: null,
      cycleSetType: null,
    };
  }
  const next = nextSetTypeInCycle(set.setType);
  return {
    ...badge,
    // The five names are the mock's bare nouns — "Robocza", "Top set" — so the badge, which is a
    // letter with nothing else to read, wraps its own into a sentence rather than announcing a
    // one-word adjective. Before 7 September 2026 the label carried the noun itself ("Seria
    // robocza") and the menu one control away said it six times in five rows.
    label: t(`${KEY}.setTypeBadgeA11y`, { label: t(`${KEY}.setType.${badge.kind}`) }),
    cycleLabel: t(`${KEY}.setTypeCycle`, { type: t(`${KEY}.setType.${kindOf(next)}`) }),
    cycleSetType: next,
  };
}

/** The kind key one authorable wire type maps to, for its label. */
function kindOf(setType: string): SetBadgeKind {
  if (setType === WARMUP_SET_TYPE) return 'warmup';
  if (setType === TOP_SET_TYPE) return 'top';
  if (setType === BACKOFF_SET_TYPE) return 'backoff';
  if (setType === AMRAP_SET_TYPE) return 'amrap';
  return 'working';
}

/**
 * The type menu for one row — five entries, always, one per authorable type.
 *
 * Back-off is the entry that is not a relabel: a descent is a percentage OF something, so a top has
 * to exist first. The menu used to answer "no top yet" by listing every set that could become one
 * plus a choice that minted a fresh top — four ways to add one back-off. The owner threw that out
 * (3 Aug 2026): **you add a top, and then you may add a back-off.** So the entry is single, and on
 * an exercise with no top it is REFUSED — dimmed, still focusable, its description carrying the
 * reason — exactly how mobile's own sheet treats the row it will not take.
 *
 * Every entry carries its badge and one line of explanation, because the menu is the surface where a
 * trainer meets a type they have not used before — mobile's own "Typ serii" list is shaped the same
 * way, and the wording of the shared lines is its wording.
 */
export function setTypeChoices(
  t: Translate,
  exercise: ExerciseDraft,
  set: SetDraft,
): SetTypeChoiceView[] {
  /** The badge one CHOICE wears. Every choice names a type this build authors, so it is always known. */
  const badge = setTypeBadge;

  /** The one place a row's three visible parts become the one string a screen reader hears. */
  const named = (choice: Omit<SetTypeChoiceView, 'ariaLabel'>): SetTypeChoiceView => ({
    ...choice,
    ariaLabel: choice.isRefused
      ? t(`${KEY}.setTypeRefusedA11y`, { label: choice.label, description: choice.description })
      : choice.isCurrent
        ? t(`${KEY}.setTypeCurrentA11y`, { label: choice.label, description: choice.description })
        : t(`${KEY}.setTypeA11y`, { label: choice.label, description: choice.description }),
  });

  const relabels: Omit<SetTypeChoiceView, 'ariaLabel'>[] = [
    WARMUP_SET_TYPE,
    WORKING_SET_TYPE,
    TOP_SET_TYPE,
    AMRAP_SET_TYPE,
  ].map((setType) => ({
    key: setType,
    label: t(`${KEY}.setType.${kindOf(setType)}`),
    letter: badge(setType).letter,
    kind: badge(setType).kind,
    description: t(`${KEY}.setTypeDesc.${kindOf(setType)}`),
    setType,
    isCurrent: set.setType === setType,
    isRefused: false,
  }));

  const backoffBadge = badge(BACKOFF_SET_TYPE);
  // The row that already IS a back-off is never refused — an inbound ladder with no top is a shape
  // the table already flags (`ladderMissingTop`), and a menu that refused to describe the row's own
  // type would be the chooser failing to say what it is looking at.
  const isCurrentBackoff = set.setType === BACKOFF_SET_TYPE;
  const hasTop = exercise.sets.some(isTopSet);
  const refused = !hasTop && !isCurrentBackoff;
  const backoffs: Omit<SetTypeChoiceView, 'ariaLabel'>[] = [
    {
      key: 'backoff',
      label: t(`${KEY}.setType.backoff`),
      letter: backoffBadge.letter,
      kind: backoffBadge.kind,
      description: refused
        ? t(`${KEY}.setTypeDesc.backoffNeedsTop`)
        : t(`${KEY}.setTypeDesc.backoff`),
      setType: BACKOFF_SET_TYPE,
      isCurrent: isCurrentBackoff,
      isRefused: refused,
      topSetIndex: null,
    },
  ];

  // Back-off sits where the cycle puts it — after Top — so the menu and the badge tell the same
  // story about what follows what.
  return [...relabels.slice(0, 3), ...backoffs, ...relabels.slice(3)].map(named);
}

/** What one cell knows about the cycle the form is open on. `null` in the builder. */
interface CellCycle {
  readonly row: CycleSetView;
  readonly cycleIndex: number;
  /** The exercise's own first active cycle — the coordinate a pin is judged against. */
  readonly baseCycle: number;
}

/** The contract and the wording of one cell. */
function toCell(
  t: Translate,
  locale: string,
  column: ResolvedGridColumn,
  exercise: ExerciseDraft,
  sets: readonly SetDraft[],
  set: SetDraft,
  rowLabel: string,
  cycle: CellCycle | null,
): SetCellView {
  const percentMode = isPercentMode(exercise.prescriptionMode);
  const isWeight = column.field.key === 'weight';
  const isRest = column.field.key === 'rest';
  const load = isWeight
    ? setLoadDisplay(set, sets, {
        measuresWeight: hasWeightField(exercise.measurementFields),
        isPercentMode: percentMode,
      })
    : null;
  const isLadderCell = load?.kind === 'percentOfTop';
  const isPercentCell = isWeight && (percentMode || isLadderCell);

  const valueKey = resolveValueKey(column.field, isPercentCell);
  const contract: CellContract = {
    valueKey,
    field: column.measurementField,
    isPercent: isPercentCell,
    isRest,
  };

  const raw = readCellValue(set, valueKey);
  const definition: { readonly unit: string | null; readonly step: number | null } | null =
    column.measurementField === null ? null : measurementFieldDefinition(column.measurementField);
  // The header says "Ciężar"; a ladder row's own cell says "% topu". The two differ ON PURPOSE, and
  // only the cell's version reaches a screen reader — a reader inside the cell cannot see the
  // header, and being told "Ciężar" over a cell holding 85 would be wrong by a factor of the top.
  const name = isWeight ? loadColumnLabel(t, percentMode, isLadderCell) : t(column.field.labelKey);

  // The one cell the exercise PROGRESSES on — the only one a cycle can hold its own value for.
  const progressing = cycle !== null && cycle.row.pinField === valueKey ? cycle : null;

  return {
    key: column.field.key,
    contract,
    text: formatCellNumber(raw, contract, locale),
    ariaLabel: `${name}, ${rowLabel}`,
    suffix: isWeight
      ? loadSuffix(t, percentMode, isLadderCell)
      : isRest
        ? null
        : (definition?.unit ?? null),
    derived:
      load?.kind === 'percentOfTop' && load.estimateKg !== null
        ? t(`${KEY}.derivedKilograms`, { value: formatPoints(locale, load.estimateKg) })
        : null,
    emphasis: column.field.emphasis,
    inputMode: isRest ? 'text' : definition?.step !== null && definition?.step !== 1 ? 'decimal' : 'numeric',
    resolvedNote:
      progressing !== null && progressing.row.resolvesInSession
        ? t(`${KEY}.resolvesInSession`)
        : null,
    pin:
      progressing !== null && progressing.row.pinnable
        ? { cycleIndex: progressing.cycleIndex, baseCycle: progressing.baseCycle }
        : null,
  };
}

/** "Ciężar" / "% 1RM" / "% topu" — the one column whose header is not one-to-one with a field. */
function loadColumnLabel(t: Translate, percentMode: boolean, isLadderCell: boolean): string {
  if (isLadderCell) return t(`${KEY}.field.percentOfTop`);
  return percentMode ? t(`${KEY}.field.percentOfOneRm`) : t(`${KEY}.field.weight`);
}

function loadSuffix(t: Translate, percentMode: boolean, isLadderCell: boolean): string {
  if (isLadderCell) return t(`${KEY}.unit.percentOfTop`);
  return percentMode ? t(`${KEY}.unit.percentOfOneRm`) : t(`${KEY}.unit.kilograms`);
}

function toSetRow(
  t: Translate,
  locale: string,
  exercise: ExerciseDraft,
  sets: readonly SetDraft[],
  set: SetDraft,
  columns: readonly ResolvedGridColumn[],
  cycle: CellCycle | null,
): SetRowView {
  const label = t(`${KEY}.setRow`, { index: set.setIndex });
  const removable = canRemoveSet(exercise);
  return {
    // The row's own identity, never its position.
    //
    // This was `slotToken-setIndex`, and `setIndex` is a POSITION that every insert and delete
    // renumbers. Deleting the first of four sets therefore removed the right row and animated the
    // FOURTH one out: the key that vanished from the list was the highest, never the one the
    // trainer pressed. `localId` is minted per set and carried through every renumber, so a row
    // keeps its key while its number changes underneath it.
    key: set.localId,
    setIndex: set.setIndex,
    label,
    badge: badgeOf(t, set),
    typeChoices: setTypeChoices(t, exercise, set),
    cells: columns.map((column) => toCell(t, locale, column, exercise, sets, set, label, cycle)),
    warning: hasAmrapCapConflict(set)
      ? t(`${KEY}.amrapTwoCaps`, {
          kept: t(`${KEY}.field.${survivingAmrapCap(exercise.measurementFields)}`),
        })
      : null,
    duplicate: { label: t(`${KEY}.duplicateSet`), disabledReason: null },
    remove: {
      label: t(`${KEY}.removeSet`),
      disabledReason: removable ? null : t(`${KEY}.removeSetRefused`),
    },
    actionsLabel: t(`${KEY}.setActions`, { index: set.setIndex }),
    typeMenuTitle: t(`${KEY}.setTypeTitle`),
  };
}

/** Which fixed track a column occupies in the gallery's grid. */
function trackOf(field: SetGridFieldKey): ColumnTrack {
  if (field === 'weight') return 'weight';
  if (field === 'reps') return 'reps';
  if (field === 'rest') return 'rest';
  if (field === 'rpe' || field === 'rir') return 'score';
  return 'extra';
}

/**
 * What one exercise's view reads besides the exercise itself: the catalogue, the words and the day's
 * rest. The day form hands its whole input; a card drawn outside a day (`exerciseChange.ts`) hands
 * the rest it falls back to, which is the plan's default when it knows no day.
 */
export type ExerciseViewContext = Omit<DayProgrammingInput, 'day'> & {
  readonly day: Pick<DayDraft, 'restSeconds'>;
};

export function toExerciseView(
  input: ExerciseViewContext,
  exercise: ExerciseDraft,
  ordinal: string | null,
  isWarmup: boolean,
  /** Whether this exercise already holds a warm-up of its own — see `addWarmup`. */
  hasWarmup: boolean,
): ExerciseView {
  const { t, locale } = input;
  const fields = exerciseColumns(exercise);
  const columns = resolveGridColumns(fields, exercise.showsRestColumn);

  // The exercise's OWN lifespan inside the phase, so the projection is measured from its activation
  // rather than from the phase's first cycle, and a cycle the exercise sits out advances nothing.
  const lifespan =
    input.cycle == null
      ? null
      : exerciseLifespan(exercise, input.cycle.phaseStartCycle, input.cycle.phaseCycleCount);
  const cycleRows =
    lifespan === null || input.cycle == null
      ? null
      : exerciseAtCycle(exercise, lifespan, input.cycle.cycleIndex);
  // The sets AS THIS CYCLE PRESCRIBES THEM. Every reader below takes this list rather than
  // `exercise.sets`, including the ladder's own sibling lookup — a descent's kilogram estimate is
  // read off the top set, and reading week 1's top under week 5's descent would be a wrong number
  // presented as a derivation.
  const sets = cycleRows?.map((row) => row.set) ?? exercise.sets;
  const cycleAt = (index: number): CellCycle | null => {
    const row = cycleRows?.[index];
    const baseCycle = lifespan === null ? null : baseCycleOf(lifespan);
    if (row === undefined || baseCycle === null || input.cycle == null) return null;
    return { row, cycleIndex: input.cycle.cycleIndex, baseCycle };
  };

  const group = isWarmup
    ? null
    : displayGroupOf(input.attribution.get(exercise.exerciseId)?.primary[0]);
  const typeKey = input.typeLabels.get(exercise.exerciseId) ?? null;
  const dayRest = dayRestSeconds(input.day.restSeconds);
  const percentMode = isPercentMode(exercise.prescriptionMode);
  // Which columns carry their own `×`.
  //
  // EVERY one of them, since the 07.09 programator: its header puts a `kill` on each `extraCols`
  // entry unconditionally, so the mark stops meaning "the trainer added this" and starts meaning
  // "this column can be closed". The 02.08 gallery drew it on added columns alone, and reading
  // "standing" off that header is what the STANDING set here used to do.
  //
  // The one column with no `×` is the LAST one: closing it would leave a table with no measurement
  // in it. That refusal is drawn as absence here and worded in "Parametry serii", whose own entry
  // stays, locked, carrying the reason — so nothing is refused in silence.

  // The exercise's own share of the day's load. Scoped to ITSELF rather than read out of the day's
  // module: the day's percentages are of the day's total, and this row's are of this exercise's.
  const contribution = isWarmup
    ? []
    : (muscleLoadByDay(
        [[{ exerciseId: exercise.exerciseId, countingSetCount: countingSetCount(exercise.sets) }]],
        input.attribution,
      ).days[0]?.entries ?? []);

  const rows = sets.map((set, index) =>
    toSetRow(t, locale, exercise, sets, set, columns, cycleAt(index)),
  );

  return {
    key: exercise.slotToken,
    slotToken: exercise.slotToken,
    ordinal,
    name: exercise.name,
    thumbnailUrl: input.thumbnails.get(exercise.exerciseId) ?? null,
    typeLabel: typeKey === null ? null : t(typeKey),
    muscle: group === null ? null : { group, label: muscleLabel(t, group) },
    isWarmup,
    columns: columns.map((column) => ({
      key: column.field.key,
      label:
        column.field.key === 'weight'
          ? loadColumnLabel(t, percentMode, false)
          : t(column.field.labelKey),
      emphasis: column.field.emphasis,
      track: trackOf(column.field.key),
      close:
        columns.length <= 1 ? null : columnAction(t, exercise, column.field.key, dayRest),
    })),
    setColumnLabel: t(`${KEY}.field.setIndex`),
    typeColumnLabel: t(`${KEY}.field.setType`),
    actionsColumnLabel: t(`${KEY}.rowActionsColumn`),
    rows,
    // An exercise whose whole contract is `Tempo` — or one that measures nothing at all — has no
    // numeric cell to open. Said in words rather than drawn as an empty grid.
    noColumns:
      columns.length === 0
        ? {
            label: t(`${KEY}.noColumns`),
            hint: t(`${KEY}.noColumnsHint`),
            hintLabel: t(`${KEY}.noColumnsHintLabel`),
          }
        : null,
    ladderWarning: backoffBlockMissingTop(exercise.sets) ? t(`${KEY}.ladderMissingTop`) : null,
    note: {
      value: exercise.notes ?? '',
      label: t(`${KEY}.exerciseNoteLabel`, { name: exercise.name }),
      hint: t(`${KEY}.exerciseNoteHint`),
      maxLength: TEXT_LIMITS.exerciseNotes.max,
    },
    addSet: {
      label: t(`${KEY}.addSet`),
      disabledReason:
        exercise.sets.length >= COLLECTION_CAPS.setsPerExercise.max
          ? t(`${KEY}.addSetRefused`, { max: COLLECTION_CAPS.setsPerExercise.max })
          : null,
    },
    menuLabel: t(`${KEY}.exerciseMenu`, { name: exercise.name }),
    isPercentMode: percentMode,
    measuresWeight: hasWeightField(exercise.measurementFields),
    showsRestColumn: exercise.showsRestColumn,
    columnMenu: toColumnMenu(t, exercise, fields, dayRest),
    unit:
      columns.some((column) => column.field.key === 'weight') &&
      hasWeightField(exercise.measurementFields)
        ? {
            label: loadSuffix(t, percentMode, false),
            toggleLabel: percentMode
              ? t(`${KEY}.menu.toKilograms`)
              : t(`${KEY}.menu.toPercentOfOneRm`),
          }
        : null,
    contribution: contribution.map((entry) => ({
      group: entry.group,
      label: muscleLabel(t, entry.group),
      points: formatPoints(locale, entry.points),
      barPercent: Math.round(entry.barFraction * 100),
    })),
    contributionTitle: t(`${KEY}.contribution`),
    collapsedSummary: collapsedSummary(t, sets.length, columns.length, rows[0] ?? null),
    fold: {
      collapse: t(`${KEY}.collapseExercise`, { name: exercise.name }),
      expand: t(`${KEY}.expandExercise`, { name: exercise.name }),
    },
    supersetPick: {
      label: t(`${KEY}.supersetPick`, { name: exercise.name }),
      // A warm-up is the ONE refusal. An exercise already inside a bracket is eligible since
      // 7 September 2026 (owner): the join ABSORBS it, so ticking a member of an existing pair
      // beside a third card makes one three-member block instead of a second bracket next to the
      // first. See `groupAsSuperset`.
      refusalReason: isWarmup ? t(`${KEY}.supersetPickWarmup`) : null,
    },
    hasDedicatedWarmup: hasWarmup,
    addWarmup:
      isWarmup || hasWarmup
        ? null
        : {
            label: t(`${KEY}.exerciseWarmup`),
            ariaLabel: t(`${KEY}.exerciseWarmupFor`, { name: exercise.name }),
          },
  };
}

/**
 * "3 × 10", from the set count and the FIRST column of the first row.
 *
 * The first column is the one the dictionary puts first — reps for a press, `Czas` for a plank —
 * so the summary states what the exercise actually prescribes instead of naming a repetition
 * column a plank does not have. An empty table has nothing to summarise and says nothing.
 */
function collapsedSummary(
  t: Translate,
  setCount: number,
  columnCount: number,
  firstRow: SetRowView | null,
): string | null {
  if (setCount === 0 || columnCount === 0 || firstRow === null) return null;
  const first = firstRow.cells[0];
  if (first === undefined || first.text.trim() === '') return null;
  const suffix = first.suffix === null ? '' : ` ${first.suffix}`;
  return t(`${KEY}.setsShort`, { sets: setCount, value: `${first.text}${suffix}` });
}

/**
 * The panel head's caption — "PONIEDZIAŁEK · DZIEŃ 1 Z 4".
 *
 * The POSITION is the host's to know, not the day node's: which programmed days a phase has and in
 * what order is a fact about the phase, and the form is handed that list already worded. A day the
 * list does not carry (a calendar editor open on one session) gets the weekday alone, because "day
 * 1 of 1" would be a claim about a phase this host cannot see.
 *
 * The uppercase is the stylesheet's. Keeping it out of the string is what lets a locale that does
 * not case that way still read correctly.
 */
export function dayPanelCaption(
  t: Translate,
  weekdayLabel: string | null,
  position: number | null,
  total: number,
): string | null {
  if (position === null || total <= 0) return weekdayLabel;
  if (weekdayLabel === null) return t(`${KEY}.panelCaptionNoWeekday`, { position, total });
  return t(`${KEY}.panelCaption`, { weekday: weekdayLabel, position, total });
}

/** What the panel's back and forward steps are called, and the eyebrow over the pair. */
export interface DayStepperWords {
  readonly previous: string;
  readonly next: string;
  readonly noAdjacent: string;
  readonly group: string;
}

/**
 * The stepper's nouns, chosen by whether the day the panel is open on has a weekday.
 *
 * On a calendar plan a day IS a weekday and "następny dzień w fazie" is exactly what the step
 * moves to. On a self-paced plan it is not: the canvas behind the panel numbers the CYCLE's days,
 * so a rest of one day is itself "DZIEŃ 2" and the second training is "DZIEŃ 3". A panel that also
 * said "Dzień 2 z 2" would put the same word on two different coordinates a metre apart, which
 * reads as a bug in the app rather than as two facts. Owner decision, 13 September 2026: the column
 * head keeps the mock's wording and the POSITION vocabulary gives the word up.
 *
 * The absence of a weekday is the mode signal already in scope here — it is what `dayPanelCaption`
 * above branches on — so nothing new has to be plumbed to know which plan this is.
 */
export function dayStepperWords(t: Translate, weekdayLabel: string | null): DayStepperWords {
  if (weekdayLabel !== null) {
    return {
      previous: t(`${KEY}.previousDay`),
      next: t(`${KEY}.nextDay`),
      noAdjacent: t(`${KEY}.noAdjacentDay`),
      group: t(`${KEY}.adjacentDays`),
    };
  }
  return {
    previous: t(`${KEY}.previousTraining`),
    next: t(`${KEY}.nextTraining`),
    noAdjacent: t(`${KEY}.noAdjacentTraining`),
    group: t(`${KEY}.adjacentTrainings`),
  };
}

/** "Zwiń wszystkie" / "Rozwiń wszystkie", by what pressing it would do. */
export function collapseAllLabel(t: Translate, isCollapsed: boolean): string {
  return isCollapsed ? t(`${KEY}.expandAll`) : t(`${KEY}.collapseAll`);
}

/** The superset selection bar, once the trainer has turned the mode on. */
export interface SupersetModeView {
  /**
   * The toggle in the head. ONE word in both states — the fill and `aria-pressed` carry whether the
   * mode is on, which is what a toggle button's state is for. See `supersetModeView`.
   */
  readonly toggleLabel: string;
  /** The sentence under the head: the instruction under two picks, the count at two or more. */
  readonly sentence: string;
  /** True once the bar is stating a count rather than asking for one — the ink steps up with it. */
  readonly isReady: boolean;
  readonly cancelLabel: string;
  readonly joinLabel: string;
  /**
   * Why the join cannot act — fewer than two picks. `null` when it can.
   */
  readonly joinRefusalReason: string | null;
}

/**
 * The words the selection mode speaks.
 *
 * `count` is what the panel has ticked, and the panel is responsible for it holding only tokens the
 * day still offers — which cards are eligible is a question each card answers for itself through
 * `supersetPick`, and asking it twice here is how the bar and the checkboxes would end up
 * disagreeing about what may be joined. See `useDayPanelChrome`, which reconciles the selection.
 *
 */
export function supersetModeView(t: Translate, count: number): SupersetModeView {
  const isReady = count >= 2;
  return {
    // The WORD does not change when the mode turns on (mock, 7 September 2026). "Zakończ łączenie
    // w superset" roughly doubled the chip's width and shoved the two controls beside it every time
    // a trainer opened the mode; the fill and `aria-pressed` are what carry the state, which is what
    // a toggle button's state is for.
    toggleLabel: t(`${KEY}.supersetMode`),
    sentence: isReady ? t(`${KEY}.supersetPicked`, { count }) : t(`${KEY}.supersetHint`),
    isReady,
    cancelLabel: t(`${KEY}.supersetCancel`),
    joinLabel: t(`${KEY}.supersetJoin`),
    joinRefusalReason: isReady ? null : t(`${KEY}.supersetJoinRefused`),
  };
}

/**
 * What the live region says once a join has HAPPENED — never before it.
 *
 * A function rather than a field on the view, because the count that matters is the one the
 * operation reported joining rather than the one the bar was showing. The two can differ by a
 * deletion, an unlink or a role change made with the bar still up, and announcing "Połączono w
 * superset: 2" over an unchanged plan is worse than saying nothing at all.
 */
export function supersetJoinedMessage(t: Translate, count: number): string {
  return t(`${KEY}.supersetJoined`, { count });
}

/** The i18n stem for the confirm each column asks before it takes its values away. */
function removeConfirmStem(field: SetGridFieldKey): string {
  if (field === 'weight') return `${KEY}.removeColumn.weight`;
  if (field === 'rest') return `${KEY}.removeColumn.rest`;
  return `${KEY}.removeColumn.other`;
}

/**
 * Does closing this column DISCARD something the trainer authored?
 *
 * Only that answer decides whether the confirm appears. A column standing open with nothing behind
 * it — one the trainer opened a moment ago and never filled — has nothing to lose, and a dialog
 * over it would be a question with one possible answer.
 *
 * REST is the odd one, as everywhere: every set always carries a concrete rest, so "holds a value"
 * is never the question. What is lost is only the per-set intervals that DIFFER from the day's, and
 * that is what `overridesDayRest` asks.
 */
function removingDiscardsValues(
  exercise: ExerciseDraft,
  field: SetGridFieldKey,
  dayRest: number,
): boolean {
  if (field === 'rest') return overridesDayRest(exercise.sets, dayRest);
  if (field === 'weight') return hasAuthoredLoad(exercise.sets);
  const valueKey = measurementValueKey(field);
  return valueKey !== null && exercise.sets.some((set) => readCellValue(set, valueKey) !== null);
}

/** One column, worded, with the question closing it has to ask first. */
function columnAction(
  t: Translate,
  exercise: ExerciseDraft,
  field: SetGridFieldKey,
  dayRest: number,
): ColumnActionView {
  const label =
    field === 'weight'
      ? loadColumnLabel(t, isPercentMode(exercise.prescriptionMode), false)
      : t(`${KEY}.field.${field}`);
  return {
    field,
    label,
    confirm: removingDiscardsValues(exercise, field, dayRest)
      ? {
          title: t(`${removeConfirmStem(field)}.title`, { column: label }),
          description: t(`${removeConfirmStem(field)}.body`, { column: label, seconds: dayRest }),
          confirmLabel: t(`${removeConfirmStem(field)}.confirm`),
          cancelLabel: t(`${removeConfirmStem(field)}.cancel`),
        }
      : null,
  };
}

/**
 * The "Parametry serii" panel — every column the exercise could carry, ticked or not.
 *
 * ── Why one list rather than the two the ⋯ used to hold ──────────────────────────────────────
 * The 02.08 gallery draws this behind the table's own "+ Parametr" header, as a checkbox list with
 * a sentence under it: "Wyłączony parametr znika z tabeli w całej fazie — wpisane wartości zostają
 * zapamiętane." That is exactly what the two-list version said in two headings, and it says it in
 * one place with the current state visible rather than inferred from which list an entry is in.
 *
 * ── Why the columns still ship in BOTH directions ────────────────────────────────────────────
 * Unchanged, and it is the rule that matters: values ARE the column state, so a column cleared with
 * no way back is gone for good in this session and every one after it. Every toggle is reversible.
 *
 * REST is one of the toggles rather than a control of its own, for the same reason it always was:
 * to the trainer it is a column like the others, and two paths to one act is how the two drift.
 *
 * `Reps` is ticked and LOCKED. The wire requires it (`PostSetInput.PrescribedReps`), so a control
 * that could turn it off would be a control that produces a save the backend refuses — and the
 * gallery draws exactly that, a ticked box at .45 opacity beside the word "wymagany".
 */
function toColumnMenu(
  t: Translate,
  exercise: ExerciseDraft,
  visibleFields: readonly MeasurementFieldKey[],
  dayRest: number,
): ColumnMenuView {
  const visible = new Set<SetGridFieldKey>(visibleFields);
  if (exercise.showsRestColumn) visible.add('rest');

  // REST sits after the visible metrics, which is where the gallery draws it — fourth of nine, under
  // Powtórzenia / Ciężar / RPE. It is listed exactly once whether or not its column is open: it is a
  // column like the others to the trainer, and `visible` above is what decides its tick.
  const addable = addableFields(exercise.measurementFields, visibleFields);
  const everything: SetGridFieldKey[] = [...visibleFields, 'rest', ...addable];

  return {
    triggerLabel: t(`${KEY}.menu.addParameter`),
    title: t(`${KEY}.menu.parameters`),
    hint: t(`${KEY}.menu.parametersHint`),
    toggles: everything.map((field) => {
      const action = columnAction(t, exercise, field, dayRest);
      return {
        field,
        label: action.label,
        hint: parameterHint(t, field),
        isOn: visible.has(field),
        // A SENTENCE, not the shape word beside it: this is what the entry says under its own label
        // when it cannot act, and "wymagany" on its own is a column note rather than a refusal.
        lockedReason: field === 'reps' ? t(`${KEY}.menu.parameterRequired`) : null,
        confirm: action.confirm,
      };
    }),
  };
}

/** The shape the gallery prints beside a parameter — "kg lub % 1RM", "3-1-1". `null` where it does not. */
function parameterHint(t: Translate, field: SetGridFieldKey): string | null {
  if (field === 'weight') return t(`${KEY}.menu.parameterHint.weight`);
  // Reps carried "wymagany" here until 7 September 2026, when the entry started drawing its
  // `lockedReason` under its own label. This column is the SHAPE note beside a name — "kg lub % 1RM",
  // "3-1-1" — and one row saying "required" twice in two registers is a statement, not a shape.
  if (field === 'rir') return t(`${KEY}.menu.parameterHint.rir`);
  if (field === 'rest') return null;
  return measurementFieldDefinition(field).unit;
}

/**
 * The ⋯ menu for one exercise, worded — including WHY an entry cannot act.
 *
 * The two guards arrive as parameters rather than being read here: both are plan-scoped questions
 * the component subscribes to per row (there is no stable row count to call a hook for), and the
 * entity owns the rules. What this adds is the words, and a test can reach them.
 *
 * Two acts LEFT this menu on 2 Aug 2026 and are named here so the history reads straight: opening
 * and closing a column moved to the table's own "+ Parametr" header, and the kg ↔ %1RM switch moved
 * into the load column's header. Both are where the 02.08 gallery draws them, and neither is in two
 * places — two paths to one act is how the two drift.
 *
 * ── The entries print a VERB, and the sentence they used to print moved (8 September 2026) ───
 * Owner: the menu had become a wall of text — "Połącz w superset z następnym" over two lines with an
 * explanation under it, three times over. The label is one or two words now, and the two things the
 * long form was carrying are split rather than dropped:
 *
 * The role entry reads "Do rozgrzewki" rather than "Rozgrzewka", and that is not a stray extra word:
 * the card head beside it now carries a `Rozgrzewka` CHIP that does something else entirely — it
 * gives this movement its own warm-up, where this moves the movement into the DAY's warm-up run. One
 * word on two controls with two meanings is a defect the shortening would otherwise have introduced.
 *
 * · **A refusal** is `disabledReason`, and it is no longer DRAWN. `ExerciseMenu` puts it on
 *   `aria-describedby`, so the entry stays focusable, `aria-disabled` and fully explained to a
 *   screen reader while the menu stays two words wide.
 * · **What the act did** is `done`, announced into the dock's live region afterwards. It is not the
 *   accessible name: a name has to CONTAIN the visible label (WCAG 2.5.3) and Polish inflection
 *   makes that impossible here — "Przenieś do rozgrzewki" does not contain "Rozgrzewka". So the name
 *   IS the label, and the sentence is a `role="status"` message instead, which has no such rule.
 */
export interface MenuActionView extends ActionView {
  /** What to announce after the act. Never the accessible name — see above. */
  readonly done: string;
}

/**
 * A running client plan, for the one act that has to be confirmed on one.
 *
 * `null` on a library template, which deletes an exercise on the press as it always has — there is
 * no client downstream of it and nothing to warn about.
 */
export interface LiveRemovalContext {
  /** A self-paced plan counts CYCLES, so the consequence sentence names cycles, not weeks. */
  readonly unitIsCycle: boolean;
}

export interface ExerciseMenuView {
  readonly group: MenuActionView;
  readonly ungroup: MenuActionView;
  readonly toggleRole: MenuActionView;
  /**
   * `confirm` is non-null exactly on a running plan: removing a movement from a plan a client is on
   * changes what they are given next, so it is answered before it happens — mobile's
   * `RemoveExerciseConfirm`, in the same words, from the shared locale.
   */
  readonly remove: MenuActionView & {
    /**
     * The trash's accessible NAME, which carries the movement.
     *
     * `label` is the bare verb and stays the pointer's tooltip. A day panel drawing five cards drew
     * five controls called "Usuń z dnia" — one name for five different acts, which voice control
     * cannot pick between and a screen reader cannot tell apart. The card's own ⋯ has carried the
     * movement in its name since 8 September 2026 for exactly this reason; the trash beside it did
     * not, because removal came out of that menu and the naming did not come with it.
     */
    readonly name: string;
    readonly confirm: ConfirmPrompt | null;
  };
}

export function exerciseMenuView(
  t: Translate,
  exercise: ExerciseView,
  guards: {
    readonly canGroupWithNext: boolean;
    readonly isGrouped: boolean;
    readonly removalRefusal: ExerciseRemovalRefusal | null;
    readonly live: LiveRemovalContext | null;
  },
): ExerciseMenuView {
  return {
    group: {
      label: t(`${KEY}.menu.group`),
      done: t(`${KEY}.menu.groupDone`),
      disabledReason: guards.canGroupWithNext ? null : t(`${KEY}.menu.groupRefused`),
    },
    ungroup: {
      label: t(`${KEY}.menu.ungroup`),
      done: t(`${KEY}.menu.ungroupDone`),
      disabledReason: guards.isGrouped ? null : t(`${KEY}.menu.ungroupRefused`),
    },
    toggleRole: {
      label: exercise.isWarmup ? t(`${KEY}.menu.toMain`) : t(`${KEY}.menu.toWarmup`),
      done: exercise.isWarmup ? t(`${KEY}.menu.toMainDone`) : t(`${KEY}.menu.toWarmupDone`),
      disabledReason: null,
    },
    remove: {
      label: t(`${KEY}.menu.remove`),
      name: t(`${KEY}.menu.removeOn`, { name: exercise.name }),
      // Said after the act, like every other entry of this menu. It was the control's own LABEL,
      // so a screen-reader user who confirmed the removal heard "Usuń z dnia" — the button they had
      // already pressed — rather than what had happened to the day.
      done: t(`${KEY}.menu.removeDone`, { name: exercise.name }),
      disabledReason:
        guards.removalRefusal === null ? null : t(`${KEY}.menu.removeRefused`),
      confirm: guards.live === null ? null : removeExerciseConfirm(t, exercise, guards.live),
    },
  };
}

/**
 * What removing this movement from a running plan is about to do, in mobile's own sentences.
 *
 * Four bodies, and the pair that differs is the lift that takes its own warm-up with it. The keys
 * are the SHARED locale's — `plans.builder.removeExerciseConfirm*` — because the trainer reads the
 * same consequence on both platforms and one wording is the point of the shared file.
 *
 * One sentence is appended to all four, and it is this app's: the removal is undoable until the plan
 * is saved. The phone's wording cannot carry that, because the phone has no undo — it writes its
 * edit and the sentence would be false there. The day delete one layer up appends the same line,
 * from the same key.
 */
function removeExerciseConfirm(
  t: Translate,
  exercise: ExerciseView,
  live: LiveRemovalContext,
): ConfirmPrompt {
  const body = exercise.hasDedicatedWarmup
    ? 'removeExerciseConfirmBodyWithWarmup'
    : 'removeExerciseConfirmBody';
  return {
    title: t('plans.builder.removeExerciseConfirmTitle'),
    subject: exercise.name,
    description: `${t(`plans.builder.${body}${live.unitIsCycle ? 'Cycle' : ''}`)} ${t(
      'web:planBuilder.undoHint',
    )}`,
    confirmLabel: t('plans.builder.removeExerciseConfirmConfirm'),
    cancelLabel: t('plans.builder.removeExerciseConfirmCancel'),
    destructiveConfirm: true,
  };
}

/**
 * How many ROWS the warm-up run adds to the table without adding to the set counter.
 *
 * The gallery's own footnote is arithmetic — "25 serii przy 29 wierszach jest poprawne" — so the
 * sentence only helps if both numbers are the day's real ones. Warm-up sets are rows; they are
 * never counted work (`setCounting.ts`, and the domain rule that only `Warmup` does not count).
 */
function warmupSetCount(exercises: readonly ExerciseView[]): number {
  return exercises.reduce((total, exercise) => total + exercise.rows.length, 0);
}

/**
 * The whole form.
 *
 * @returns Every string the dock prints and every control it offers. A component below this reads
 *   only this value — no `t`, no `Intl`, no arithmetic, no `day.exercises[i]`.
 */
export function dayProgrammingView(input: DayProgrammingInput): DayProgrammingView {
  const { day, t, locale } = input;
  // One suffix rather than a branch at each pill: the two hints answer the same question about the
  // same host, and reading it twice is how they would end up disagreeing.
  const dropHintSuffix = input.libraryIsModal === true ? 'Press' : '';
  const card = dayCard(day);
  const byToken = new Map(day.exercises.map((exercise) => [exercise.slotToken, exercise]));

  const view = (
    slotToken: string,
    ordinal: string | null,
    isWarmup: boolean,
    hasWarmup = false,
  ): ExerciseView | null => {
    const exercise = byToken.get(slotToken);
    return exercise === undefined
      ? null
      : toExerciseView(input, exercise, ordinal, isWarmup, hasWarmup);
  };

  const blocks: DayBlockView[] = [];
  for (const block of card.blocks) {
    if (block.kind === 'single') {
      const exercise = view(block.exercise.slotToken, block.ordinal, block.exercise.isWarmup);
      if (exercise !== null) {
        blocks.push({ kind: 'single', key: block.exercise.slotToken, exercise, preps: null });
      }
      continue;
    }

    // ── A MINI-GROUP is one card, and its warm-ups stand inside it ──────────────────────────
    // The mock draws a lift's own ramps in a blue box above that lift's head (`prepBox`) rather than
    // as rows beside it, so the block collapses to the WORK exercise here and the warm-ups travel
    // with it as its `preps`. `toBlocks` guarantees the shape this reads — a mini-group is one or
    // more warm-ups plus exactly one work exercise — and a run that does not match it never became a
    // mini-group in the first place.
    //
    // The markers change with the drawing, and only here. The lift takes the block's plain position
    // ("2") because it is the only thing in the list holding one, and each ramp takes the bare `R`
    // the day's own warm-up run already uses, because the mock numbers no warm-up. `dayCardView` on
    // the week behind the panel still letters them `2a` / `2b`, which is the frame it draws.
    if (block.kind === 'miniGroup') {
      const work = block.members.find((member) => !member.isWarmup);
      const lift = work === undefined ? null : view(work.slotToken, block.ordinal, false, true);
      const ramps = block.members
        .filter((member) => member.isWarmup)
        .map((member) => view(member.slotToken, 'R', true))
        .filter((member): member is ExerciseView => member !== null);
      if (lift !== null && ramps.length > 0) {
        blocks.push({
          kind: 'single',
          key: lift.slotToken,
          exercise: lift,
          preps: {
            title: t(`${KEY}.prepTitle`),
            // The day's warm-up RUN already prints "2 pozycje" from this key, and the mock's
            // `prepCount` prints the same words in the same shape. One phrase, one place.
            count: t(`${KEY}.warmupCounts`, { count: ramps.length }),
            // And the name is the chip's own — "Rozgrzewka do ćwiczenia <ruch>". The chip is
            // withheld from a card that holds a box, so the two never stand together.
            label: t(`${KEY}.exerciseWarmupFor`, { name: lift.name }),
            groupLabel: t(`${KEY}.prepGroup`),
            members: ramps,
          },
        });
        continue;
      }
      // A shape `toBlocks` cannot produce, kept harmless rather than thrown: every member is drawn
      // as its own position, which is what the degenerate runs already fall back to.
      for (const member of block.members) {
        const exercise = view(member.slotToken, member.ordinal, member.isWarmup);
        if (exercise !== null) {
          blocks.push({ kind: 'single', key: member.slotToken, exercise, preps: null });
        }
      }
      continue;
    }

    const members = block.members
      .map((member) => view(member.slotToken, member.ordinal, member.isWarmup))
      .filter((member): member is ExerciseView => member !== null);
    if (members.length === 0) continue;
    blocks.push({
      kind: 'superset',
      key: members[0]?.slotToken ?? block.ordinal,
      ordinal: block.ordinal,
      label: t('web:planBuilder.superset'),
      // The gallery spells the frame out — "2a → 2b bez przerwy". The members' own markers are
      // what it names, so the sentence is built from them rather than from a fixed pair.
      note: t(`${KEY}.supersetNote`, {
        members: members.map((member) => member.ordinal ?? '').join(' → '),
      }),
      members,
    });
  }

  // The warm-up run. Every row's marker is a bare `R`, as the programator draws it — the 02.08
  // gallery numbered them `R1`…`R4`, and the mock does not, because the run is unordered work the
  // athlete does before the day rather than a sequence of positions to refer to. The POSITION is
  // still spoken: the row's accessible name carries the movement, and the run is a list.
  const warmupExercises = (card.warmup?.exercises ?? [])
    .map((row) => view(row.slotToken, 'R', true))
    .filter((exercise): exercise is ExerciseView => exercise !== null);

  const load = muscleLoadByDay([card.countedExercises], input.attribution).days[0];
  const sets = t('web:planBuilder.sets', { count: card.countingSetCount });
  const exercises = t('web:planBuilder.exercises', { count: card.mainExerciseCount });
  const rest = dayRestSeconds(day.restSeconds);

  return {
    slotToken: day.slotToken,
    weekdayLabel: weekdayLabel(locale, day.dayOfWeek),
    title: card.title ?? t('web:planBuilder.untitledSession'),
    authoredTitle: card.title,
    nameFieldLabel: t('web:planBuilder.sessionNameLabel'),
    nameMaxLength: TEXT_LIMITS.sessionName.max,
    // The duration joins only when one was authored — nothing here estimates one, and "~0 min"
    // would be a lie about a session nobody measured.
    counts:
      card.durationMinutes == null
        ? t(`${KEY}.counts`, { exercises, sets })
        : t(`${KEY}.countsWithDuration`, { exercises, sets, minutes: card.durationMinutes }),
    warmup: {
      title: t('web:planBuilder.warmup'),
      counts: t(`${KEY}.warmupCounts`, { count: warmupExercises.length }),
      footnote: t(`${KEY}.warmupFootnote`, {
        sets: card.countingSetCount,
        rows: card.countingSetCount + warmupSetCount(warmupExercises),
      }),
      footnoteLabel: t(`${KEY}.warmupFootnoteLabel`),
      exercises: warmupExercises,
      drop: {
        label: t(`${KEY}.addWarmup`),
        hint: t(`${KEY}.addWarmupHint${dropHintSuffix}`),
      },
      menuLabel: t(`${KEY}.warmupMenu`),
    },
    mainTitle: t('web:planBuilder.mainWork'),
    mainCounts: t(`${KEY}.counts`, { exercises, sets }),
    blocks,
    muscles:
      load === undefined
        ? []
        : load.entries.map((entry) => ({
            group: entry.group,
            label: muscleLabel(t, entry.group),
            points: formatPoints(locale, entry.points),
            percent: t('web:planBuilder.percent', { value: entry.percent }),
            barPercent: Math.round(entry.barFraction * 100),
          })),
    muscleTitle: t('web:planBuilder.loadTitle'),
    muscleTotal: t('web:planBuilder.points', {
      value: formatPoints(locale, load?.totalPoints ?? 0),
    }),
    sessionNote: {
      value: day.sessionNotes ?? '',
      label: t(`${KEY}.sessionNoteLabel`),
      placeholder: t(`${KEY}.sessionNotePlaceholder`),
      maxLength: TEXT_LIMITS.sessionNotes.max,
    },
    dayRest: {
      label: t(`${KEY}.dayRestLabel`),
      hint: t(`${KEY}.dayRestHint`),
      hintLabel: t(`${KEY}.dayRestHintLabel`),
      text: clockDuration(rest),
      seconds: rest,
    },
    mainDrop: {
      label: t(`${KEY}.addExercise`),
      hint: t(`${KEY}.addExerciseHint${dropHintSuffix}`),
    },
    // A day with nothing in it at all — a "+ Trening" that has not been filled yet. `draftToRequest`
    // drops such a day, so the hint is also the sentence explaining why saving changes nothing.
    emptyDay:
      day.exercises.length === 0
        ? { title: t(`${KEY}.emptyDayTitle`), hint: t(`${KEY}.emptyDay`) }
        : null,
    scopeNote: input.cycle == null ? t(`${KEY}.phaseScope`) : t(`${KEY}.cycleScope`),
    cycleNote: input.cycle == null ? null : t(`${KEY}.cycleNote`),
  };
}
